import { CircularProgress } from "@mui/material";
import { CSSProperties } from "@mui/styles";
import { useTranslation } from "react-i18next";
import NewMainSelect from "components/NewMainSelect/NewMainSelect.view";
import { DropdownOptions, IOption } from "../Dropdown";

export type SegmentFieldVariant = "inline" | "modal";

interface ISegmentSelectProps {
    /** "inline" → compact table-edit control (DropdownOptions); "modal" → CBO form dropdown (NewMainSelect). */
    variant: SegmentFieldVariant;
    value?: number;
    options: IOption[];
    onChange: (id: number) => void;
    loading?: boolean;
    obligatory?: boolean;
    disabled?: boolean;
    pending?: boolean;
    placeholder?: string;
    inlineStyle?: CSSProperties;
    className?: string;
}

/**
 * Presentation primitive shared by the SegmentFields. It lets the same field logic
 * (option-fetching + cascade) render two different looks: the compact inline control
 * used in the Details table edit, and the full-width labelled dropdown used in the
 * Split modal. Only the rendered control differs.
 */
export const SegmentSelect = ({
    variant,
    value,
    options,
    onChange,
    loading,
    obligatory,
    disabled,
    pending,
    placeholder,
    inlineStyle,
    className,
}: ISegmentSelectProps) => {
    const { t } = useTranslation();

    if (variant === "modal") {
        // While options load, force an empty value so NewMainSelect shows the placeholder
        // (otherwise a pre-filled value renders blank until its option/label arrives).
        return (
            <NewMainSelect
                options={options}
                value={loading ? "" : value ?? ""}
                onChange={(selected) => onChange(Number(selected))}
                placeholder={loading ? t("data.loading") : placeholder}
                disabled={disabled || loading}
                fullWidth
                className={className}
            />
        );
    }

    if (loading) {
        return <CircularProgress size={20} color="info" />;
    }

    return (
        <DropdownOptions
            obligatory={obligatory}
            pending={pending}
            disabled={disabled}
            inlineStyle={inlineStyle ?? { width: "80px" }}
            optionSelected={value}
            options={options}
            handleUpdate={async (id: number) => onChange(id)}
        />
    );
};
